import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import useSelector
import { selectCurrentUser } from '../../../features/auth/authSlice'; // Import selectCurrentUser
import { useGetPropertyQuery } from '../../../features/api/apiSlice'; // Import useGetPropertyQuery
import Header from '../../../components/ui/Header';
import Footer from '../../../components/ui/Footer';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import HostelBookingForm from './HostelBookingForm';
import { useToast } from '../../../components/ui/Toast'; // Import useToast
import SEO from '../../../components/seo';

const HostelDetail = () => {
  const { slug_id } = useParams();
  const navigate = useNavigate();
  const addToast = useToast(); // Initialize useToast
  const currentUser = useSelector(selectCurrentUser); // Get current user from Redux store

  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Fetch hostel data using the generic getProperty endpoint
  const { data: apiHostel, isLoading, isError, error } = useGetPropertyQuery(slug_id, {
    skip: !slug_id, // Skip query if slug_id is not available
  });

  const hostel = apiHostel; // Use the fetched data as the hostel object

  // Handle API errors with toasts
  useEffect(() => {
    if (isError) {
      addToast(`Error fetching hostel details: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [isError, error, addToast]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const getAvailabilityColor = (available, total) => {
    const percentage = (available / total) * 100;
    if (percentage >= 50) return 'text-green-600 bg-green-100';
    if (percentage >= 25) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading hostel details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !hostel) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Hostel not found</h1>
            <p className="text-muted-foreground">The hostel you are looking for does not exist or an error occurred.</p>
            <Button onClick={() => navigate('/hostels')}>Back to Hostels</Button>
          </div>
        </div>
      </div>
    );
  }

  // Mocked data for fields not directly available in generic property API
  const mockHostelDetails = {
    university: "Makerere University", // Assuming this can be derived or added to property model
    totalRooms: 20, // Mocked
    availableRooms: 10, // Mocked
    rating: 4.0, // Mocked
    reviews: 0, // Mocked
    roomTypes: [ // Mocked
      { type: "Single Room", capacity: 1, price: hostel.price, currency: "UGX", period: "per month", available: 5, features: ["Private bathroom", "Study desk", "Wardrobe", "WiFi included"], image: hostel.title_image },
      { type: "Double Room", capacity: 2, price: hostel.price * 0.8, currency: "UGX", period: "per person/month", available: 5, features: ["Shared bathroom", "Study desks", "Lockers", "WiFi included"], image: hostel.title_image }
    ],
    facilities: [ // Mocked
      { name: "Academic Support", description: "Study rooms, computer labs, and library access", icon: "BookOpen" },
      { name: "Health & Wellness", description: "On-campus medical center and counseling services", icon: "Heart" },
    ],
    rules: [ // Mocked
      "Students must be enrolled", "Valid ID required", "Quiet hours apply"
    ],
    contact: { // Mocked
      manager: "Hostel Manager", phone: "+256 123 456 789", email: "info@hostel.com", office: "Hostel Office"
    },
    location: { // Mocked
      address: hostel.address, coordinates: { lat: hostel.latitude, lng: hostel.longitude }, nearby: ["Campus Canteen", "Bookshop"]
    }
  };

  // Merge API data with mock data for missing fields
  const displayHostel = {
    ...mockHostelDetails,
    ...hostel,
    images: hostel.gallery?.map(img => img.image_url) || [hostel.title_image], // Use gallery images if available
    name: hostel.title, // Map title to name
    location: {
      ...mockHostelDetails.location,
      address: hostel.address,
      coordinates: { lat: parseFloat(hostel.latitude), lng: parseFloat(hostel.longitude) }
    }
  };

  const hostelSchema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: displayHostel.name,
    description: displayHostel.description || 'Student accommodation and hostel room listings from HireBroker.',
    url: `https://hirebroker.co/hostels/${slug_id}`,
    image: displayHostel.title_image,
    address: {
      '@type': 'PostalAddress',
      streetAddress: displayHostel.address || '',
      addressLocality: displayHostel.city || '',
      addressCountry: 'UG'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: displayHostel.rating || 4,
      reviewCount: displayHostel.reviews || 0
    }
  };

  if (showBookingForm) {
    return <HostelBookingForm hostel={displayHostel} onBack={() => setShowBookingForm(false)} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${displayHostel.name} | Hostel Rental`}
        description={displayHostel.description || `Browse this hostel rental listing on HireBroker and contact our broker team for help.`}
        canonicalUrl={`https://hirebroker.co/hostels/${slug_id}`}
        ogImage={displayHostel.title_image}
        schema={hostelSchema}
      />
      <Header />

      <main className="pt-16">
        {/* Hero Section with Image Gallery */}
        <div className="relative">
          <div className="h-96 md:h-[500px] relative overflow-hidden">
            <img
              src={displayHostel.images[selectedImage]}
              alt={displayHostel.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute bottom-4 left-4 right-4 flex gap-2 overflow-x-auto">
              {displayHostel.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${
                    index === selectedImage ? 'border-white' : 'border-white/50'
                  }`}
                >
                  <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Hostel Info Overlay */}
          <div className="absolute top-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">{displayHostel.name}</h1>
                <p className="text-muted-foreground">{displayHostel.university}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center">
                    {renderStars(displayHostel.rating)}
                    <span className="ml-2 text-sm">({displayHostel.reviews} reviews)</span>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getAvailabilityColor(displayHostel.availableRooms, displayHostel.totalRooms)}`}>
                    {displayHostel.availableRooms}/{displayHostel.totalRooms} rooms available
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/hostels')}>
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to Hostels
                </Button>
                <Button onClick={() => setShowBookingForm(true)}>
                  <Icon name="Calendar" size={16} className="mr-2" />
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="bg-card rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">About This Hostel</h2>
                <p className="text-muted-foreground leading-relaxed">{displayHostel.description}</p>
              </div>

              {/* Room Types */}
              <div className="bg-card rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6">Room Types & Pricing</h2>
                <div className="space-y-6">
                  {displayHostel.roomTypes.map((room, index) => (
                    <div key={index} className="border border-border rounded-lg p-4">
                      <div className="flex flex-col md:flex-row gap-4">
                        <img
                          src={room.image}
                          alt={room.type}
                          className="w-full md:w-32 h-24 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-semibold">{room.type}</h3>
                              <p className="text-sm text-muted-foreground">
                                {room.capacity} person{room.capacity > 1 ? 's' : ''} • {room.available} available
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">
                                {room.currency} {room.price.toLocaleString()}
                              </div>
                              <p className="text-sm text-muted-foreground">{room.period}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {room.features.map((feature, idx) => (
                              <span key={idx} className="px-2 py-1 bg-muted text-xs rounded-full">
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Facilities */}
              <div className="bg-card rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6">Campus Facilities</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayHostel.facilities.map((facility, index) => (
                    <div key={index} className="flex items-start p-4 border border-border rounded-lg">
                      <Icon name={facility.icon} size={24} className="text-primary mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-foreground">{facility.name}</h3>
                        <p className="text-sm text-muted-foreground">{facility.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div className="bg-card rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Amenities & Services</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {displayHostel.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <Icon name="Check" size={16} className="text-green-500 mr-2" />
                      <span className="text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hostel Rules */}
              <div className="bg-card rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Hostel Rules & Regulations</h2>
                <div className="space-y-3">
                  {displayHostel.rules.map((rule, index) => (
                    <div key={index} className="flex items-start">
                      <Icon name="AlertCircle" size={16} className="text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{rule}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Booking */}
              <div className="bg-card rounded-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold mb-4">Ready to Book?</h3>
                <Button
                  className="w-full mb-4"
                  onClick={() => setShowBookingForm(true)}
                  disabled={displayHostel.availableRooms === 0}
                >
                  {displayHostel.availableRooms === 0 ? 'Fully Booked' : 'Start Booking Process'}
                </Button>

                {/* Availability Status */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>Room Availability</span>
                    <span className={`font-medium px-2 py-1 rounded-full text-xs ${getAvailabilityColor(displayHostel.availableRooms, displayHostel.totalRooms)}`}>
                      {displayHostel.availableRooms}/{displayHostel.totalRooms}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        (displayHostel.availableRooms / displayHostel.totalRooms) * 100 >= 50 ? 'bg-green-500' :
                        (displayHostel.availableRooms / displayHostel.totalRooms) * 100 >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(displayHostel.availableRooms / displayHostel.totalRooms) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Complete booking process takes ~5 minutes
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-card rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-foreground">{displayHostel.contact.manager}</p>
                    <p className="text-sm text-muted-foreground">Hostel Manager</p>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Phone" size={16} className="text-muted-foreground mr-2" />
                    <span className="text-sm">{displayHostel.contact.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Mail" size={16} className="text-muted-foreground mr-2" />
                    <span className="text-sm">{displayHostel.contact.email}</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="MapPin" size={16} className="text-muted-foreground mr-2 mt-0.5" />
                    <span className="text-sm">{displayHostel.contact.office}</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-card rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Location & Nearby</h3>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{displayHostel.location.address}</p>
                  <div>
                    <p className="font-medium text-sm mb-2">Nearby Facilities:</p>
                    <div className="flex flex-wrap gap-1">
                      {displayHostel.location.nearby.map((place, index) => (
                        <span key={index} className="px-2 py-1 bg-muted text-xs rounded-full">
                          {place}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HostelDetail;
